import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context';

import { usePlacementApi } from '@post-office/use-placement-api';

import type { CustomRenderer } from '@post-office/message-maker-browser/types';

import { useCallback, useEffect, useState } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

// This type will be the contract between the message and the placement
export type placementRenderProps = {
	startTour: (onDismiss: () => void) => void;
};

type placementDefinition = CustomRenderer<placementRenderProps>;

type AppProps = { startTour: (onDismiss: () => void) => void };

const placementConfig: PlacementBrowserConfig = {
	placementId: 'share-and-permissions-modal',
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
		hideInIsolatedCloud: false,
	},
};

const placement = {
	createFrontend: createPlacementFrontend<placementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<placementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<placementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<placementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>((Messages) => ({ startTour }: AppProps) => {
	const [message, setMessage] = useState<EligibleMessage>();
	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage>;
	}>('/');

	useEffect(() => {
		if (!data?.messages.length || error || isLoading) {
			return;
		}

		for (const message of data.messages) {
			if (message.messageTemplateId === 'confluence-share-modal-spotlight-tour') {
				// oxlint-disable-next-line post-office/no-manual-in-app-experiments -- Legacy experiment handling is retained without migration.
				const cohort = FeatureGates.getExperimentValue<string>(
					'share_and_perms_e2e_feature_gate_ab',
					'cohort',
					'not-enrolled',
					{ fireExperimentExposure: true },
				);

				if (cohort !== 'variation') {
					continue;
				}
			}

			setMessage(message);
			break;
		}
	}, [data?.messages, error, isLoading]);

	const handleDismiss = useCallback(() => {
		setMessage(undefined);
	}, []);

	const handleStartTour = useCallback(
		(messageOnDismiss: () => void) => {
			messageOnDismiss();
			startTour(handleDismiss);
		},
		[startTour],
	);

	if (error) {
		throw error;
	}

	if (isLoading || !message) {
		return null;
	}

	return <Messages.render {...message} startTour={handleStartTour} />;
});

export type PlacementFrontendProps = {
	startTour: (onDismiss: () => void) => void;
};
